/**
 * From a problem+json `code` to a sentence somebody working a register can act on.
 *
 * Mapped on `code`, never on the status.
 *
 * **`conflict` is deliberately vague here, and that is why `detailOf` exists.** The tasks
 * domain answers `409` to at least three different situations — an illegal status jump, the
 * verification gate naming who has to close a task, and a label whose word the project already
 * has — and the server writes a sentence for each, in the person's own terms. So the caller
 * prints the server's sentence when there is one and falls back to this generic line only when
 * there is not. A client-side table of nine conflict wordings would be a second implementation
 * of a rule that already has one, and it would be the one that goes stale.
 *
 * `filter_not_allowed` earns its own line: it means a `saved=` value this build does not know
 * about, which is a stale tab rather than anything the person did wrong.
 */

import { ApiError } from '@/api/client';
import type { MessageKeys } from '@/lib/i18n';

type Key = MessageKeys['tasks'];

/** Codes from `ErrorCode` in `backend/src/kalqa/core/errors.py`. TWIN: that enum. */
const BY_CODE: Readonly<Record<string, Key>> = {
  forbidden: 'error.forbidden',
  not_found: 'error.gone',
  conflict: 'error.conflict',
  validation_failed: 'error.validation',
  rate_limited: 'error.rateLimited',
  cursor_invalid: 'error.cursor',
  filter_not_allowed: 'error.filter',
  csrf_failed: 'error.unexpected',
};

export function taskErrorKey(error: unknown): Key {
  if (!(error instanceof ApiError)) return 'error.unexpected';
  return BY_CODE[error.code] ?? 'error.unexpected';
}

/**
 * A bulk-edit skip (`TaskBulkSkip.code`), in the person's language.
 *
 * A bulk edit answers `200` with the rows it applied and the rows it skipped, each skip
 * carrying an `ErrorCode`; this is the one map from that code to a sentence. It lived in
 * `TaskHierarchy.tsx` until the register row's assignee picker (`MCK-20260521-008`) needed
 * the same words for a one-task edit the server declined.
 */
export function bulkSkipKey(code: string): Key {
  switch (code) {
    case 'task_closed':
      return 'bulk.skip.closed';
    case 'invalid_transition':
      return 'bulk.skip.transition';
    case 'forbidden':
      return 'bulk.skip.forbidden';
    case 'verification_required':
      return 'bulk.skip.verification';
    default:
      return 'bulk.skip.gone';
  }
}

/**
 * The server's own sentence, when it has one worth showing.
 *
 * Preferred over the generic line for every `409` on this screen: the two the board can meet —
 * an illegal transition, and the verification gate — are rendered at the card, in the server's
 * words, because only the server knows which rule refused and who has to close it (§C).
 */
export function detailOf(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  const detail: unknown = error.problem.detail;
  return typeof detail === 'string' && detail !== '' ? detail : null;
}

/**
 * `true` when the representation this screen was editing has moved on (`412`).
 *
 * Deliberately not a line in `BY_CODE`: a stale edit is not an error to report and move on
 * from — the detail screen refetches the task so the person edits what is actually there now,
 * and the sentence it prints is the server's own (`detailOf`), which names the conflict.
 */
export function isStale(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'stale_resource';
}

/**
 * The attach pipeline's failures, in the shapes the screen has different sentences for.
 *
 * Two error families arrive here because the mutation is two acts: `UploadError` from the
 * ticket flow (`features/drawings/upload.ts` — hash, transfer, scan), then a possible
 * `ApiError` from `POST /tasks/{id}/attachments`. The second goes through `detailOf` and
 * `taskErrorKey` like every other API refusal; this maps only the first.
 */
export function attachFailureKey(failure: string): Key {
  switch (failure) {
    case 'insecure_context':
      return 'attachment.insecure';
    case 'rejected':
      return 'attachment.rejected';
    case 'timed_out':
      return 'attachment.timedOut';
    default:
      return 'attachment.failed';
  }
}
